import { useMutation } from "@apollo/client";
import { useRecoilState } from "recoil";
import renderStatusModal from "../Forms/renderStatusModal";
import { useDebouncedFunction } from "./useDebouncedFunction";
import { getErrorDetails } from "../Utils/getErrorDetails";
import { profilePrefillMetadataState } from "../SimpleForm/Atoms/Profiles/state";

/**
 * @example
 * ```
 * const saveForm = useSaveForm({
 *    formSaveMutation,
 *    formSaveMutationCallback,
 *    formValues: values,
 *    form_ui,
 *    tablePagesErrors,
 *    delay: 1000,
 * });
 * ```
 *
 * @param {Object} props - The props object.
 * @param {DocumentNode} props.formSaveMutation - The mutation name (e.g., `UPDATE_790_FORM`)
 * @param {( formValues, formUIData, saveForm ) => void} props.formSaveMutationCallback - The callback function prop that accepts `args(formValues, formUIData, saveForm)`, and runs the mutation function with `variables`
 *
 * Example Prop:
 * ```
 *   `formSaveMutationCallback={({
 *     formValues,
 *     formUIData,
 *     saveForm,
 *   }) => {
 *     saveForm({
 *       variables: {
 *         formUI790A: formUIData,
 *         Form790AFormData: cleanData(formValues),
 *         id: application_id,
 *       },
 *     });
 *    }}`
 * ```
 * @param {Object} props.formValues - The form values from `form API {}`
 * @param {Object} props.form_ui - The form UI data (e.g., `{ pages_visited: [0,1,7], pages_errors: [0,1,7] }`)
 * @param {number[]} props.form_ui.pages_visited - Array of pages visited `[]`
 * @param {number[]} props.form_ui.pages_errors - Array of pages errored `[]`
 * @param {number[]} props.tablePagesErrors - Array of table pages errors, to be combined into pages_errors `[]`
 * @param {number} props.delay - The debounce delay in milliseconds (e.g., `500`)
 */

export function useSaveForm({
  formSaveMutation,
  formSaveMutationCallback = () => {},
  formValues = {},
  form_ui,
  tablePagesErrors = [],
  delay = 0,
}) {
  const [profilePrefillMetadata, setProfilePrefillMetadata] = useRecoilState(
    profilePrefillMetadataState
  );

  // Mutation hook for form saving
  const [saveForm, { error }] = useMutation(formSaveMutation, {
    onCompleted: () => {
      renderStatusModal(true);
      setProfilePrefillMetadata((prev) => ({
        ...prev,
        profiles: [],
      }));
    },
    onError: () => {
      const { duration, message } = getErrorDetails(error);
      renderStatusModal(false, message, duration);
      setProfilePrefillMetadata((prev) => ({
        ...prev,
        profiles: [],
      }));
    },
  });

  // Debounces the form saving
  const debouncedSaveForm = useDebouncedFunction(() => {
    const formUIData = {
      pages_visited: form_ui.pages_visited,
      pages_errors: [
        ...new Set([...form_ui["pages_errors"], ...tablePagesErrors]),
      ],
    };

    // Callback to top level parent component, to perform the saving mutation.
    // By providing additional data (formValues, formUIData) and the mutation function (saveForm).
    formSaveMutationCallback({
      formValues,
      formUIData,
      saveForm,
      profilePrefillMetadata,
    });
  }, delay);

  // The returned, debounced mutation function to execute the form saving.
  return debouncedSaveForm;
}
